import React from 'react'
import AntdCard from 'antd/es/card'
import type { CardProps } from 'antd/es/card'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Card as ShadcnSeamCard } from './Card.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/card` — one of the six presentational
 * components put behind the design toggle in the same PR (Input, Card, Alert,
 * Divider, Empty, Skeleton), after `./Button` (#970), `./Typography` +
 * `./Space` (#971) and `./Tag` (#972).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it. `update-design` ships a real seam at this
 * same path with the same shape — a NAMED `Card` and no default — so a
 * redirected call site needs no further change when that lands.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders antd's own
 * Card with the props it was given and the ref it was given: same element,
 * same class names, same DOM. The only difference from what the barrel handed
 * back before — antd's own component — is one extra component in the React
 * tree, which renders nothing of its own.
 * `./__tests__/card-seam-parity.test.tsx` asserts that byte for byte across a
 * prop matrix.
 *
 * antd's two statics, `Card.Grid` and `Card.Meta`, are NOT carried across, and
 * that is measured rather than assumed: no file in this repo names either
 * (0 call sites each). A dispatched passthrough for a surface nothing
 * exercises is a second thing to keep true; if a call site ever appears, it
 * goes here beside the root, on the same pattern as `Skeleton.Image`.
 * ------------------------------------------------------------------------- */
const CardDispatcher = React.forwardRef<HTMLDivElement, CardProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') return <ShadcnSeamCard {...props} ref={ref} />

    return <AntdCard {...props} ref={ref} />
  }
)

CardDispatcher.displayName = 'Card'

export const Card = CardDispatcher
